import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import * as fromStore from "../../../store";
import {Store} from "@ngrx/store";
import {Subject} from "rxjs";
import {combineLatest} from "rxjs";
import {UserService} from "../../../user/user.service";
import {first, switchMap, takeUntil} from "rxjs/operators";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {User} from "../../../user/user";
import {AuthService} from "../../../auth/auth.service";
import {UserSubscriptionType} from "../../../user/user-subscription";
import {DbActiveModal} from "../../../modules/modal/modal-ref";

@Component({
    selector: 'db-migration-success-dialog',
    templateUrl: './migration-success-dialog.component.html',
    styleUrls: ['./migration-success-dialog.component.scss'],
    standalone: false
})
export class MigrationSuccessDialogComponent implements OnInit, OnDestroy {

  @Input() oldData;
  @Input() newData;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private websiteId: number;
  private userLocalStorage: any;
  public hostingInfoData: any;
  public siteUrl: string = '';
  public websiteScreenshot: string = '';
  public scores: { type: string, score: number, time: number }[];
  public user: User;
  public isMobile: boolean;

  constructor(
    private _activeModal: DbActiveModal,
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>,
    private _userService: UserService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _authService: AuthService,
  ) {
  }

  ngOnInit() {
    this._activeModal.resize(600, 1000);
    this._activeModal.whiteCloseBtn(true, 0.5);
    this._sendPopupVisibleGAEvent();
    this.websiteId = this.newData && this.newData.websiteId;
    this.isMobile = window.innerWidth <= 991;

    this.setScores(this.oldData, this.newData);
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage)
        )),
        first(),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userLocalStorage]) => {
        this.user = user;
        this.userLocalStorage = userLocalStorage;
        this.saveInLocalStorage();
      });
    this.websiteId && combineLatest(
      this._store.select(fromStore.getWebsite(this.websiteId)),
      this._store.select(fromStore.getInfo(this.websiteId))
    )
      .pipe(
        takeUntil(this._destroyStream$),
        first(([website, hostingInfo]) => !!website && !!hostingInfo),
      )
      .subscribe(([website, hostingInfo]) => {
        this.hostingInfoData = hostingInfo || null;
        this.siteUrl = website && website.site_url || '';
        this.websiteScreenshot = website && website.screenshot || '';
      });

  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private setScores(oldData, newData): void {
    this.scores = [
      {
        type: 'mobile',
        score: oldData ? oldData.mobileScore : null,
        time: oldData ? oldData.mobileTTI : null
      },
      {
        type: 'mobile',
        score: newData ? newData.mobileScore : null,
        time: newData ? newData.mobileTTI : null
      },
      {
        type: 'desktop',
        score: oldData ? oldData.score : null,
        time: oldData ? oldData.desktopTTI : null
      },
      {
        type: 'desktop',
        score: newData ? newData.score : null,
        time: newData ? newData.desktopTTI : null
      },
    ];
  }

  public saveInLocalStorage(): void {
    if (this.websiteId) {
      this.userLocalStorage['hideMigrationSuccess' + this.websiteId] = true;
      this._userService.setToLocal(this.userLocalStorage)
        .pipe(
          first(response => !!response)
        )
        .subscribe(response => {
          if (response.success) {
            this._store.dispatch(new fromStore.LoadUserLocalStorage());
          }
        });
    }
    if (this.user && this.user.signUpFor && !this.user.signUpFor.Action) {
      this.user.signUpFor['Action'] = {answer: `migrate-from-${this.userLocalStorage['migrateFromPage']}`, id: ''};
    }
    this._authService.updateSignUpFor(this.user.signUpFor, true);
  }

  public close() {
    this._router.navigateByUrl(`/websites/${this.websiteId}/main`);
    this._activeModal.close();
  }

  private _sendPopupVisibleGAEvent(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Migration Success Popup',
      eventLabel: 'Migration Success Popup'
    };
    this.googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
